import { useMemo, memo } from 'react';
import {
    Box,
    Flex,
    Text,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */

const NAVY        = '#1B3B5F';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
// const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
// const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   HELPERS
   ───────────────────────────────────────────────────────────── */

const getMaxPrice = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) return 0;
    const max = items.reduce((acc, item) => {
        const r = Number(item?.rate ?? 0);
        return Number.isFinite(r) && r > acc ? r : acc;
    }, 0);
    return Math.ceil(max); /* whole NGN, no fractional kobo on the slider */
};

const formatNGN = (n) => {
    const num = Number(n) || 0;
    return `₦${num.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
};

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */

function FilterSlider({ sliderValue, setSliderValue }) {
    const { products } = useSelector((state) => state.productReducer);
    const items = products?.items;

    const maxValue = useMemo(() => getMaxPrice(items), [items]);
    const hasRange = maxValue > 0;

    /* When no products are loaded yet, show a disabled placeholder
       instead of returning null — the section header would otherwise
       look broken with empty content beneath it. */
    if (!hasRange) {
        return (
            <Box py="6px">
                <Text fontSize="11px" color={FAINT} fontFamily={FONT}>
                    Price range will appear once products load.
                </Text>
            </Box>
        );
    }

    const isAtMin = sliderValue === 0;

    return (
        <Box pt="6px" pb="4px">
            {/* Current value row — shows what the user is filtering BY */}
            <Flex
                align="center"
                justify="space-between"
                mb="14px"
                gap="8px"
            >
                <Box>
                    <Text fontSize="9px" color={FAINT} fontWeight="700" letterSpacing="0.1em" mb="2px">
                        MAX PRICE
                    </Text>
                    <Text fontSize="14px" fontWeight="800" color={INK} fontFamily={FONT} lineHeight="1.1">
                        {isAtMin ? 'Any' : formatNGN(sliderValue)}
                    </Text>
                </Box>
                {!isAtMin && (
                    <Text
                        as="button"
                        type="button"
                        fontSize="10px"
                        fontWeight="700"
                        color={NAVY}
                        onClick={() => setSliderValue(0)}
                        _hover={{ textDecoration: 'underline' }}
                        bg="transparent"
                        border="none"
                        cursor="pointer"
                        flexShrink={0}
                    >
                        Clear
                    </Text>
                )}
            </Flex>

            {/* The slider itself */}
            <Slider
                aria-label="Maximum price filter"
                min={0}
                max={maxValue}
                step={Math.max(1, Math.round(maxValue / 100))}
                value={sliderValue}
                onChange={(val) => setSliderValue(val)}
                focusThumbOnChange={false}
            >
                <SliderTrack
                    bg={BORDER}
                    h="4px"
                    borderRadius="full"
                >
                    <SliderFilledTrack bg={NAVY} />
                </SliderTrack>
                <SliderThumb
                    boxSize="16px"
                    bg={SURFACE}
                    border="3px solid"
                    borderColor={NAVY}
                    boxShadow="0 1px 3px rgba(27,59,95,0.2)"
                    _focusVisible={{
                        boxShadow: `0 0 0 4px ${CYAN}`,
                    }}
                />
            </Slider>

            {/* Min/Max range labels — inline below track */}
            <Flex justify="space-between" mt="8px">
                <Text fontSize="10px" color={FAINT} fontWeight="600" fontFamily={FONT}>
                    {formatNGN(0)}
                </Text>
                <Text fontSize="10px" color={FAINT} fontWeight="600" fontFamily={FONT}>
                    {formatNGN(maxValue)}
                </Text>
            </Flex>
        </Box>
    );
}

export default memo(FilterSlider);